import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [creditPacks, setCreditPacks] = useState([]);
  const [daysUntilChristmas, setDaysUntilChristmas] = useState(0);

  useEffect(() => {
    const loadCreditPacks = async () => {
      try {
        const response = await api.get('/payments/credit-packs');
        setCreditPacks(response.data.packs);
      } catch (error) {
        console.error('Failed to load credit packs:', error);
      }
    };
    loadCreditPacks();

    // Calculate days until Christmas
    const now = new Date();
    const currentYear = now.getFullYear();
    let christmas = new Date(currentYear, 11, 25);
    if (now > christmas) {
      christmas = new Date(currentYear + 1, 11, 25);
    }
    const diffTime = christmas - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysUntilChristmas(diffDays);
  }, []);

  return (
    <div className="main-content">
      <div className="hero">
        <h1 className="hero-title"><span className="tree-icon">🌲</span> Jingle Texts</h1>
        <p className="hero-slogan">SMS straight from the North Pole</p>
        <p className="hero-subtitle">
          Send magical Christmas messages from Santa, the North Pole, or your 
          friendly Elf on the Shelf! Surprise your loved ones with festive SMS messages 
          that spread holiday cheer! ✨
        </p>
        <div className="countdown-badge">
          <span>🎄</span>
          <span><span className="days">{daysUntilChristmas}</span> {daysUntilChristmas === 1 ? 'sleep' : 'sleeps'} until Christmas</span>
        </div>
        <div style={{ marginTop: '30px' }}>
          {isAuthenticated ? (
            <Link to="/send" className="btn btn-primary" style={{ fontSize: '1.2rem', padding: '15px 40px' }}>
              🎁 Send a Message
            </Link>
          ) : (
            <>
              <Link to="/signup" className="btn btn-primary" style={{ fontSize: '1.2rem', padding: '15px 40px', marginRight: '15px' }}>
                🎄 Get Started
              </Link>
              <Link to="/login" className="btn btn-outline" style={{ fontSize: '1.2rem', padding: '15px 40px' }}>
                Login
              </Link>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px' }}>
        <div className="card shimmer-border">
          <h3 className="card-title">🎅 From Santa</h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            Send personalised messages as Santa Claus himself! Perfect for children 
            who believe in the magic of Christmas.
          </p>
        </div>

        <div className="card shimmer-border">
          <h3 className="card-title">🎄 From North Pole</h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            Official communications from Santa's workshop! Weather reports, 
            naughty/nice list updates, and special delivery notices.
          </p>
        </div>

        <div className="card shimmer-border">
          <h3 className="card-title">🧝 From Elf on Shelf</h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            Let your family's Elf send secret messages! Daily reports to Santa, 
            mischief alerts, and goodbye messages.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginTop: '40px' }}>
        <h2 className="card-title">✨ How It Works</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '30px', marginTop: '20px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '10px' }}>1️⃣</div>
            <h4 style={{ color: 'var(--christmas-gold)', marginBottom: '10px' }}>Sign Up</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Create your account in seconds
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '10px' }}>2️⃣</div>
            <h4 style={{ color: 'var(--christmas-gold)', marginBottom: '10px' }}>Get Messages</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Purchase message packs to send
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '10px' }}>3️⃣</div>
            <h4 style={{ color: 'var(--christmas-gold)', marginBottom: '10px' }}>Choose Sender</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Pick Santa, North Pole, Elf, or your own custom sender
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '10px' }}>4️⃣</div>
            <h4 style={{ color: 'var(--christmas-gold)', marginBottom: '10px' }}>Send Magic!</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Send now or schedule for later
            </p>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '40px', textAlign: 'center' }}>
        <h2 className="card-title" style={{ justifyContent: 'center' }}>🎁 Special Holiday Pricing</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '30px' }}>
          Affordable message packs to spread Christmas joy!
        </p>
        <div className="credit-packs">
          {creditPacks.map(pack => (
            <div key={pack.id} className={`credit-pack ${pack.popular ? 'popular' : ''}`}>
              <div className="credit-pack-name">{pack.name}</div>
              <div className="credit-pack-credits">{pack.credits} <span>{pack.credits === 1 ? 'message' : 'messages'}</span></div>
              <div className="credit-pack-price">£{pack.price.toFixed(2)}</div>
              <div className="credit-pack-description">{pack.description}</div>
            </div>
          ))}
        </div>
        {!isAuthenticated && (
          <Link to="/signup" className="btn btn-gold" style={{ marginTop: '30px', fontSize: '1.1rem' }}>
            🎄 Start Sending Magic Now!
          </Link>
        )}
      </div>
    </div>
  );
};

export default Home;
